import type { FC, ReactNode } from 'react'
import type { GuidePosition } from '@/lib/guides'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react'

import { cn } from '@/lib/utils'

interface GuideNavProps {
    guideSlug: string
    guideTitle: string
    guideId: string | null
    position: GuidePosition
    className?: string
}

export const GuideNav: FC<GuideNavProps> = ({
    guideSlug,
    guideTitle,
    guideId,
    position,
    className
}): ReactNode => {
    return (
        <nav aria-label='Guide navigation' className={cn('rounded-xl border border-border bg-card', className)}>
            <div className='flex flex-wrap items-center justify-between gap-3 border-b border-border p-4'>
                <p className='flex items-center gap-2 text-sm'>
                    <BookOpen className='size-4 shrink-0 text-primary' aria-hidden='true' />
                    <span className='text-muted-foreground'>
                        Part <span>{position.position}</span> of <span>{position.total}</span> in
                    </span>
                    <Link
                        href={`/guides/${guideSlug}`}
                        className='font-semibold text-primary hover:underline'
                        data-mdlfy-cms={`guides:${guideId}:title`}
                    >
                        {guideTitle}
                    </Link>
                </p>
            </div>

            <div className='grid divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0'>
                {position.previous ? (
                    <Link
                        href={`/${position.previous.hub}/${position.previous.slug}`}
                        className='group flex flex-col gap-1 p-4 transition-colors hover:bg-secondary/50'
                    >
                        <span className='inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                            <ArrowLeft className='size-3.5' aria-hidden='true' />
                            Previous
                        </span>
                        <span
                            className='text-sm font-medium leading-snug group-hover:text-primary'
                            data-mdlfy-cms={`posts:${position.previous.id}:title`}
                        >
                            {position.previous.title}
                        </span>
                    </Link>
                ) : (
                    <div className='p-4' />
                )}

                {position.next ? (
                    <Link
                        href={`/${position.next.hub}/${position.next.slug}`}
                        className='group flex flex-col gap-1 p-4 text-right transition-colors hover:bg-secondary/50'
                    >
                        <span className='inline-flex items-center justify-end gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                            Next
                            <ArrowRight className='size-3.5' aria-hidden='true' />
                        </span>
                        <span
                            className='text-sm font-medium leading-snug group-hover:text-primary'
                            data-mdlfy-cms={`posts:${position.next.id}:title`}
                        >
                            {position.next.title}
                        </span>
                    </Link>
                ) : (
                    <div className='p-4' />
                )}
            </div>
        </nav>
    )
}
